"use client";

import { PercentageRadialChart } from "./PercentageRadialChart";

interface ScoreRange {
  name: string;
  range: [number, number];
  fullRange: [number, number];
}

interface AdmissionScoresProps {
  scores: {
    satReadingWriting?: { p25: number; p75: number };
    satMath?: { p25: number; p75: number };
    actComposite?: { p25: number; p75: number };
  };
  percentSubmittingSAT?: number | null;
  percentSubmittingACT?: number | null;
  dataCycle: string;
}

export function AdmissionScores({
  dataCycle,
  scores,
  percentSubmittingSAT,
  percentSubmittingACT,
}: AdmissionScoresProps) {
  const satData: ScoreRange[] = [];
  const actData: ScoreRange[] = [];

  if (scores.satReadingWriting) {
    satData.push({
      name: "Evidence-Based Reading and Writing",
      range: [scores.satReadingWriting.p25, scores.satReadingWriting.p75],
      fullRange: [200, 800],
    });
  }

  if (scores.satMath) {
    satData.push({
      name: "Math",
      range: [scores.satMath.p25, scores.satMath.p75],
      fullRange: [200, 800],
    });
  }

  if (scores.actComposite) {
    actData.push({
      name: "Composite",
      range: [scores.actComposite.p25, scores.actComposite.p75],
      fullRange: [1, 36],
    });
  }

  if (satData.length === 0 && actData.length === 0) {
    return null;
  }

  const ScoreChart = ({ data }: { data: ScoreRange }) => {
    const fullRangeWidth = data.fullRange[1] - data.fullRange[0];
    const rangeStartPercent =
      ((data.range[0] - data.fullRange[0]) / fullRangeWidth) * 100;
    const rangeWidthPercent =
      ((data.range[1] - data.range[0]) / fullRangeWidth) * 100;

    return (
      <div className="pe-8">
        <div className="relative h-[calc(var(--spacing)*4+var(--text-sm)*var(--text-sm--line-height))]">
          {/* Background bar */}
          <div className="relative h-4 w-full overflow-hidden rounded-md bg-accent">
            {/* Range bar */}
            <div
              className="absolute top-0 h-full bg-primary"
              style={{
                left: `${rangeStartPercent}%`,
                width: `${rangeWidthPercent}%`,
              }}
            />
          </div>
          <div
            className="absolute bottom-0 flex justify-between text-sm"
            style={{
              left: `${rangeStartPercent}%`,
              width: `${rangeWidthPercent}%`,
            }}
          >
            <span className="flex -translate-x-full justify-end">
              {data.range[0]}
            </span>
            <span className="flex translate-x-full justify-start">
              {data.range[1]}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {satData.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-xl font-medium">SAT</h2>
          <div className="flex w-full flex-col items-center sm:flex-row sm:gap-8">
            {percentSubmittingSAT && (
              <PercentageRadialChart
                className="w-[140px] shrink-0"
                innerRadius={50}
                outerRadius={80}
                centerText="submitted"
                data={{
                  label: "SAT",
                  percentage: percentSubmittingSAT,
                }}
                significantDigits={0}
                centerTextClassName="text-2xl"
                centerTextSpace={20}
                labelClassName="text-xs fill-slate-400"
              />
            )}
            <div className="w-full space-y-2">
              {satData.map((data) => (
                <div key={data.name} className="space-y-2">
                  <h3 className="text-base font-normal">{data.name}</h3>
                  <ScoreChart data={data} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {actData.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-xl font-medium">ACT</h2>
          <div className="flex w-full flex-col items-center sm:flex-row sm:gap-8">
            {percentSubmittingACT && (
              <PercentageRadialChart
                className="w-[140px] shrink-0"
                innerRadius={50}
                outerRadius={80}
                centerText="submitted"
                data={{
                  label: "ACT",
                  percentage: percentSubmittingACT,
                }}
                significantDigits={0}
                centerTextClassName="text-2xl"
                centerTextSpace={20}
                labelClassName="text-xs fill-slate-400"
              />
            )}
            <div className="w-full space-y-2">
              {actData.map((data) => (
                <div key={data.name} className="space-y-2">
                  <h3 className="text-base font-normal">{data.name}</h3>
                  <ScoreChart data={data} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <span className="mx-auto text-xs text-subtle">
        The range shows 25th - 75th percentile scores of students enrolled in{" "}
        {dataCycle}
      </span>
    </div>
  );
}
